import type { TTableCellElement, TTableElement } from 'platejs';
import type { SlateElementProps } from 'platejs/static';

import { BaseTablePlugin } from '@platejs/table';
import { SlateElement } from 'platejs/static';
import * as React from 'react';
import { cn } from '../../../utils/cn';

const TableColumnCountContext = React.createContext(0);

function getElementChildren(value: unknown): Array<{ colSpan?: unknown }> {
  if (typeof value !== 'object' || value === null) {
    return [];
  }

  const { children } = value as { children?: unknown };

  return Array.isArray(children) ? children : [];
}

function getTableColumnCount(element: TTableElement) {
  return element.children.reduce((maxColumns, row) => {
    const columns = getElementChildren(row).reduce((count, cell) => {
      const colSpan = Number('colSpan' in cell ? cell.colSpan : 1);

      return count + (Number.isFinite(colSpan) && colSpan > 0 ? colSpan : 1);
    }, 0);

    return Math.max(maxColumns, columns);
  }, 0);
}

export function TableElementStatic({
  children,
  ...props
}: SlateElementProps<TTableElement>) {
  const { disableMarginLeft } = props.editor.getOptions(BaseTablePlugin);
  const marginLeft = disableMarginLeft ? 0 : props.element.marginLeft;
  const columnCount = getTableColumnCount(props.element);
  // const isTwoColumnTable = columnCount === 2;

  return (
    <SlateElement
      {...props}
      className="overflow-x-auto"
      style={{ paddingLeft: marginLeft }}
    >
      <div className="group/table relative w-full plate-viewer-table-wrapper">
        <TableColumnCountContext value={columnCount}>
          <table
            className={cn(
              'mr-0 table h-px w-full border-collapse plate-viewer-table',
              // isTwoColumnTable && '[&_tr>*:first-child]:w-[27%] [&_tr>*:first-child]:min-w-48',
            )}
          >
            <tbody className="min-w-full plate-viewer-tbody">{children}</tbody>
          </table>
        </TableColumnCountContext>
      </div>
    </SlateElement>
  );
}

export function TableRowElementStatic(props: SlateElementProps) {
  return (
    <SlateElement {...props} as="tr" className="h-full plate-viewer-tr">
      {props.children}
    </SlateElement>
  );
}

export function TableCellElementStatic({
  isHeader,
  ...props
}: SlateElementProps<TTableCellElement> & {
  isHeader?: boolean;
}) {
  const { editor, element } = props;
  const { api } = editor.getPlugin(BaseTablePlugin);
  const columnCount = React.use(TableColumnCountContext);
  const isTwoColumnTable = columnCount === 2;

  const { minHeight, width } = api.table.getCellSize({ element });
  const borders = api.table.getCellBorders({ element });
  const attributes = { ...props.attributes };
  delete attributes.colspan;
  delete attributes.rowspan;

  return (
    <SlateElement
      {...props}
      as={isHeader ? 'th' : 'td'}
      className={cn(
        'relative h-full overflow-visible p-0',
        isTwoColumnTable ? 'border-b border-c600' : 'border border-c600',
        element.background ? 'bg-(--cellBackground)' : 'bg-transparent',
        isHeader && 'text-left font-normal *:m-0',
        'before:size-full',
        'before:absolute before:box-border before:select-none before:content-[\'\']',
        isHeader ? 'plate-viewer-th' : 'plate-viewer-td',
        !isTwoColumnTable && borders
        && cn(
          borders.bottom?.size && 'before:border-b before:border-b-c600',
          borders.right?.size && 'before:border-r before:border-r-c600',
          borders.left?.size && 'before:border-l before:border-l-c600',
          borders.top?.size && 'before:border-t before:border-t-c600',
        ),
      )}
      style={
        {
          '--cellBackground': element.background,
          'maxWidth': width || 240,
          'minWidth': width || 120,
        } as React.CSSProperties
      }
      attributes={{
        ...attributes,
        colSpan: api.table.getColSpan(element),
        rowSpan: api.table.getRowSpan(element),
      }}
    >
      <div
        className={cn(
          'relative z-20 box-border h-full py-[0.46875rem] pr-2!',
          isTwoColumnTable ? 'px-0 text-c300 first:pl-0' : 'px-2',
        )}
        style={{ minHeight }}
      >
        {props.children}
      </div>
    </SlateElement>
  );
}

export function TableCellHeaderElementStatic(
  props: SlateElementProps<TTableCellElement>,
) {
  return <TableCellElementStatic {...props} isHeader />;
}
